import React from 'react';
import { Upload, FileText, ScanLine, PenLine, Sparkles } from 'lucide-react';
import { Language } from '../types';

interface EmptyNotebookOnboardingProps {
    onUploadPdf: () => void;
    onCreateBlankNote: () => void;
    language: Language;
}

const STEPS_VI = [
    { Icon: FileText,  title: 'Tải PDF lên',                    desc: 'Đọc tài liệu ngay trong StudyBoard.' },
    { Icon: ScanLine,  title: 'Capture đoạn quan trọng',         desc: 'Chụp công thức, đoạn văn hoặc hình ảnh đang học.' },
    { Icon: PenLine,   title: 'Tự giải thích bằng lời của bạn', desc: 'Biến đoạn đã đọc thành hiểu biết của riêng bạn.' },
    { Icon: Sparkles,  title: 'Dùng AI Check để kiểm tra',       desc: 'Phát hiện chỗ còn mơ hồ, thiếu bước hoặc hiểu sai.' },
];

const STEPS_EN = [
    { Icon: FileText,  title: 'Upload a PDF',                    desc: 'Read your source directly inside StudyBoard.' },
    { Icon: ScanLine,  title: 'Capture important passages',       desc: 'Save formulas, paragraphs, or diagrams from the source.' },
    { Icon: PenLine,   title: 'Explain in your own words',        desc: 'Turn what you read into your own understanding.' },
    { Icon: Sparkles,  title: 'Use AI Check',                    desc: 'Find unclear reasoning, missing steps, or misunderstandings.' },
];

export const EmptyNotebookOnboarding: React.FC<EmptyNotebookOnboardingProps> = ({
    onUploadPdf,
    onCreateBlankNote,
    language,
}) => {
    const isVi = language === 'vietnamese';
    const steps = isVi ? STEPS_VI : STEPS_EN;

    return (
        <div className="absolute inset-0 flex items-center justify-center z-10 pointer-events-none">
            <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl border border-gray-200 dark:border-slate-700 p-7 max-w-sm w-full mx-4 pointer-events-auto">
                <div className="mb-5">
                    <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-1.5">
                        {isVi ? 'Bắt đầu với một nguồn học' : 'Start with a learning source'}
                    </h2>
                    <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">
                        {isVi
                            ? 'StudyBoard hoạt động tốt nhất khi bạn học từ một tài liệu cụ thể: PDF, giáo trình, paper hoặc note bài giảng.'
                            : 'StudyBoard works best when you learn from a specific source: a PDF, textbook, paper, or lecture note.'}
                    </p>
                </div>

                <ol className="space-y-3 mb-6">
                    {steps.map(({ Icon, title, desc }, i) => (
                        <li key={i} className="flex items-start gap-3">
                            <div className="flex-shrink-0 w-7 h-7 rounded-lg bg-indigo-100 dark:bg-indigo-900/40 flex items-center justify-center mt-0.5">
                                <Icon size={14} className="text-indigo-600 dark:text-indigo-400" />
                            </div>
                            <div>
                                <span className="text-sm font-semibold text-gray-800 dark:text-gray-200">{title}</span>
                                <span className="text-xs text-gray-500 dark:text-gray-400 ml-1.5">{desc}</span>
                            </div>
                        </li>
                    ))}
                </ol>

                <div className="flex flex-col gap-2">
                    <button
                        onClick={onUploadPdf}
                        className="w-full py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-xl shadow-md hover:shadow-indigo-500/25 transition-all flex items-center justify-center gap-2 text-sm"
                    >
                        <Upload size={16} />
                        {isVi ? 'Tải PDF đầu tiên lên' : 'Upload first PDF'}
                    </button>
                    <button
                        onClick={onCreateBlankNote}
                        className="w-full py-2 text-sm text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 font-medium transition-colors"
                    >
                        {isVi ? 'Hoặc tạo note trống' : 'Or create a blank note'}
                    </button>
                </div>
            </div>
        </div>
    );
};
